import type { RefObject } from 'react';
import type { CmarkdownvisualMode } from '../Cmarkdownvisual';
import type {
  CmarkdowntableCell,
  CmarkdowntablePopoverEntry,
  CmarkdowntablePopoverPosition,
  CmarkdowntablePopoverRect,
  OpenTablePopoverRowInput,
  TablePopoverPointerHandler,
  TableRowKeyDownInput,
} from './Cmarkdowntable.types';
import { useCmarkdowntablePopoverClose } from './useCmarkdowntablePopoverClose';
import { useCmarkdowntablePopoverCopy } from './useCmarkdowntablePopoverCopy';
import { useCmarkdowntablePopoverEffects } from './useCmarkdowntablePopoverEffects';
import { useCmarkdowntablePopoverManualRect } from './useCmarkdowntablePopoverManualRect';
import { useCmarkdowntablePopoverModeTeardown } from './useCmarkdowntablePopoverModeTeardown';
import { useCmarkdowntablePopoverPointerHandlers } from './useCmarkdowntablePopoverPointerHandlers';
import { useCmarkdowntablePopoverPosition } from './useCmarkdowntablePopoverPosition';
import { useCmarkdowntablePopoverRowActions } from './useCmarkdowntablePopoverRowActions';
import {
  useCmarkdowntablePopoverState,
  type CmarkdowntablePopoverStateController,
} from './useCmarkdowntablePopoverState';

export interface UseCmarkdowntablePopoverInput {
  headerCells: CmarkdowntableCell[];
  mode: CmarkdownvisualMode;
}

export interface CmarkdowntablePopoverController {
  activeRowIndex: number | null;
  hoverRowIndex: number | null;
  setHoverRowIndex: (rowIndex: number | null) => void;
  popoverEntries: CmarkdowntablePopoverEntry[] | null;
  popoverIdValue: string;
  popoverPosition: CmarkdowntablePopoverPosition | null;
  manualRect: CmarkdowntablePopoverRect | null;
  didCopy: boolean;
  popoverRef: RefObject<HTMLDivElement | null>;
  closePopover: () => void;
  openPopoverForRow: (input: OpenTablePopoverRowInput) => void;
  handleRowKeyDown: (input: TableRowKeyDownInput) => void;
  handleHeaderPointerDown: TablePopoverPointerHandler;
  handleHeaderPointerMove: TablePopoverPointerHandler;
  handleHeaderPointerEnd: TablePopoverPointerHandler;
  handleResizePointerDown: TablePopoverPointerHandler;
  handleResizePointerMove: TablePopoverPointerHandler;
  handleResizePointerEnd: TablePopoverPointerHandler;
  handleCopyEntries: () => void;
}

interface ResolveCmarkdowntablePopoverControllerInput {
  state: CmarkdowntablePopoverStateController;
  manual: ReturnType<typeof useCmarkdowntablePopoverManualRect>;
  copy: ReturnType<typeof useCmarkdowntablePopoverCopy>;
  pointer: ReturnType<typeof useCmarkdowntablePopoverPointerHandlers>;
  closePopover: () => void;
  rowActions: ReturnType<typeof useCmarkdowntablePopoverRowActions>;
}

function resolveCmarkdowntablePopoverController(
  input: ResolveCmarkdowntablePopoverControllerInput,
): CmarkdowntablePopoverController {
  return {
    activeRowIndex: input.state.activeRowIndex,
    hoverRowIndex: input.state.hoverRowIndex,
    setHoverRowIndex: input.state.setHoverRowIndex,
    popoverEntries: input.state.popoverEntries,
    popoverIdValue: input.state.popoverIdValue,
    popoverPosition: input.state.popoverPosition,
    manualRect: input.manual.manualRect,
    didCopy: input.copy.didCopy,
    popoverRef: input.manual.popoverRef,
    closePopover: input.closePopover,
    openPopoverForRow: input.rowActions.openPopoverForRow,
    handleRowKeyDown: input.rowActions.handleRowKeyDown,
    handleHeaderPointerDown: input.pointer.handleHeaderPointerDown,
    handleHeaderPointerMove: input.pointer.handleHeaderPointerMove,
    handleHeaderPointerEnd: input.pointer.handleHeaderPointerEnd,
    handleResizePointerDown: input.pointer.handleResizePointerDown,
    handleResizePointerMove: input.pointer.handleResizePointerMove,
    handleResizePointerEnd: input.pointer.handleResizePointerEnd,
    handleCopyEntries: input.copy.handleCopyEntries,
  };
}

export function useCmarkdowntablePopover(
  { headerCells, mode }: UseCmarkdowntablePopoverInput,
): CmarkdowntablePopoverController {
  const state = useCmarkdowntablePopoverState();
  const copy = useCmarkdowntablePopoverCopy({ entries: state.popoverEntries });
  const manual = useCmarkdowntablePopoverManualRect();
  const pointer = useCmarkdowntablePopoverPointerHandlers({
    popoverRef: manual.popoverRef,
    applyManualRect: manual.applyManualRect,
  });
  const closePopover = useCmarkdowntablePopoverClose({
    resetPointerState: pointer.resetPointerState,
    resetCopyFeedback: copy.resetCopyFeedback,
    clearManualRect: manual.clearManualRect,
    setActiveRowIndex: state.setActiveRowIndex,
    setPopoverEntries: state.setPopoverEntries,
    setPopoverIdValue: state.setPopoverIdValue,
    setPopoverPosition: state.setPopoverPosition,
  });
  const { updatePopoverPosition } = useCmarkdowntablePopoverPosition({
    activeAnchorRef: manual.activeAnchorRef,
    popoverRef: manual.popoverRef,
    manualRectRef: manual.manualRectRef,
    closePopover,
    setPopoverPosition: state.setPopoverPosition,
  });
  const rowActions = useCmarkdowntablePopoverRowActions({
    activeRowIndex: state.activeRowIndex,
    headerCells,
    activeAnchorRef: manual.activeAnchorRef,
    closePopover,
    restoreRememberedManualRect: manual.restoreRememberedManualRect,
    updatePopoverPosition,
    setActiveRowIndex: state.setActiveRowIndex,
    setPopoverEntries: state.setPopoverEntries,
    setPopoverIdValue: state.setPopoverIdValue,
  });

  useCmarkdowntablePopoverEffects({
    activeRowIndex: state.activeRowIndex,
    popoverEntries: state.popoverEntries,
    activeAnchorRef: manual.activeAnchorRef,
    popoverRef: manual.popoverRef,
    manualRectRef: manual.manualRectRef,
    updatePopoverPosition,
    clampManualRectToViewport: manual.clampManualRectToViewport,
    closePopover,
  });
  useCmarkdowntablePopoverModeTeardown({ mode, closePopover });
  return resolveCmarkdowntablePopoverController({
    state,
    manual,
    copy,
    pointer,
    closePopover,
    rowActions,
  });
}
